/** A full-screen UI page (menu, dialog…) managed by ScreenManager. */
export interface Screen {
	readonly el: HTMLElement;
	/** Esc / "back" behaviour. Screens without it ignore Esc. */
	back?(): void;
	/** Called after the screen became the visible top screen. */
	shown?(): void;
	/** Called when the screen is removed for good. */
	destroy?(): void;
}

const FOCUSABLE = "button:not([disabled]), input:not([disabled]), select, [tabindex='0']";

/** Wrap-around index step used for arrow-key navigation. */
export function stepIndex(current: number, delta: number, count: number): number {
	if (count <= 0) return -1;
	if (current < 0) return delta > 0 ? 0 : count - 1;
	return (((current + delta) % count) + count) % count;
}

/**
 * Stack of DOM screens over the canvas. Keyboard-first like the original menus
 * (MENU_BTN_NEXT / PREV / PRESS): ↑/↓ move focus, Enter/Space activate, Esc goes back.
 * Mouse and touch work through the normal button semantics.
 */
export class ScreenManager {
	private readonly stack: Screen[] = [];
	private readonly lastFocus = new WeakMap<Screen, HTMLElement>();
	/** Called for Esc when no screen is open (e.g. to pause a running match). */
	onEscapeWithoutScreen: (() => void) | null = null;

	constructor(private readonly root: HTMLElement) {
		window.addEventListener("keydown", this.onKeyDown);
	}

	get top(): Screen | undefined {
		return this.stack.at(-1);
	}

	get isEmpty(): boolean {
		return this.stack.length === 0;
	}

	push(screen: Screen): void {
		const prev = this.top;
		if (prev) {
			this.rememberFocus(prev);
			prev.el.hidden = true;
		}
		this.stack.push(screen);
		this.root.append(screen.el);
		this.activate(screen);
	}

	/** Removes the top screen and returns to the one below it (if any). */
	pop(): void {
		const screen = this.stack.pop();
		if (!screen) return;
		screen.el.remove();
		screen.destroy?.();
		const prev = this.top;
		if (prev) {
			prev.el.hidden = false;
			this.activate(prev);
		}
	}

	/** Replaces the whole stack with a single screen (or nothing). */
	reset(screen?: Screen): void {
		while (this.stack.length > 0) this.pop();
		if (screen) this.push(screen);
	}

	/** Removes a specific screen wherever it is in the stack. */
	remove(screen: Screen): void {
		if (this.top === screen) {
			this.pop();
			return;
		}
		const i = this.stack.indexOf(screen);
		if (i < 0) return;
		this.stack.splice(i, 1);
		screen.el.remove();
		screen.destroy?.();
	}

	/**
	 * Sends the next key press exclusively to `handler` (for key rebinding): no menu
	 * navigation, fullscreen toggle or other listener sees it. Returns a cancel function.
	 */
	captureNextKey(handler: (ev: KeyboardEvent) => void): () => void {
		const listener = (ev: KeyboardEvent) => {
			if (!ev.code || ev.repeat) return;
			ev.preventDefault();
			ev.stopImmediatePropagation();
			cancel();
			handler(ev);
		};
		const cancel = () => window.removeEventListener("keydown", listener, true);
		window.addEventListener("keydown", listener, true);
		return cancel;
	}

	has(screen: Screen): boolean {
		return this.stack.includes(screen);
	}

	private activate(screen: Screen): void {
		const target =
			this.lastFocus.get(screen) ??
			screen.el.querySelector<HTMLElement>("[data-autofocus]") ??
			screen.el.querySelector<HTMLElement>(FOCUSABLE);
		target?.focus({ preventScroll: true });
		screen.shown?.();
	}

	private rememberFocus(screen: Screen): void {
		const active = document.activeElement;
		if (active instanceof HTMLElement && screen.el.contains(active)) {
			this.lastFocus.set(screen, active);
		}
	}

	// --- Device-independent navigation (keyboard below, gamepads via GamepadMenuNav) ---

	/** Moves focus up (-1) or down (1) in the top screen. */
	navigate(delta: number): void {
		const screen = this.top;
		if (screen) this.moveFocus(screen, delta);
	}

	/** Presses the focused button of the top screen. */
	confirm(): void {
		const screen = this.top;
		const active = document.activeElement;
		if (screen && active instanceof HTMLButtonElement && screen.el.contains(active)) active.click();
	}

	/** Back / Esc: the top screen's back action, or the no-screen handler (pause a match). */
	back(): void {
		const screen = this.top;
		if (screen) screen.back?.();
		else this.onEscapeWithoutScreen?.();
	}

	private readonly onKeyDown = (ev: KeyboardEvent): void => {
		const screen = this.top;
		if (!screen) {
			if (ev.code === "Escape" && !ev.repeat && this.onEscapeWithoutScreen) {
				ev.preventDefault();
				this.onEscapeWithoutScreen();
			}
			return;
		}
		// Let text-like controls (e.g. a name field) keep their own keys.
		const active = document.activeElement;
		const typing = active instanceof HTMLInputElement && active.type === "text";

		switch (ev.code) {
			case "Escape":
				if (ev.repeat) return;
				ev.preventDefault();
				screen.back?.();
				break;
			case "ArrowDown":
			case "ArrowUp": {
				ev.preventDefault();
				this.moveFocus(screen, ev.code === "ArrowDown" ? 1 : -1);
				break;
			}
			case "Enter":
			case "NumpadEnter":
			case "Space": {
				if (typing && ev.code === "Space") return;
				// Handled here because game input may preventDefault Space during a match.
				if (active instanceof HTMLButtonElement && screen.el.contains(active)) {
					ev.preventDefault();
					if (!ev.repeat) active.click();
				}
				break;
			}
			default:
				break;
		}
	};

	private moveFocus(screen: Screen, delta: number): void {
		const items = [...screen.el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
			(e) => e.offsetParent !== null || e === document.activeElement,
		);
		const current = items.indexOf(document.activeElement as HTMLElement);
		items[stepIndex(current, delta, items.length)]?.focus({ preventScroll: true });
	}
}
